import { useState } from 'react';
import styles from './loginpagestyles.module.css';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';

export default function TwoFactorInput({ pendingToken, onSuccess, onCancel }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pendingToken, totpCode: code.replace(/\s/g, '') }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error || t('twoFactor.wrongCode');
        const left = data.attemptsLeft != null ? ' ' + t('twoFactor.attemptsLeft', { count: data.attemptsLeft }) : '';
        setError(msg + left);
        setLoading(false);
        return;
      }
      if (data.csrfToken) {
        try { localStorage.setItem('csrfToken', data.csrfToken); } catch (_) {}
      }
      onSuccess(data);
    } catch {
      setError(t('login.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/2fa/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pendingToken, backupCode: backupCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('twoFactor.wrongBackupCode'));
        setLoading(false);
        return;
      }
      if (data.csrfToken) {
        try { localStorage.setItem('csrfToken', data.csrfToken); } catch (_) {}
      }
      onSuccess(data);
    } catch {
      setError(t('login.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.formContainer}>
        <h2 style={{ marginBottom: '8px' }}>{t('twoFactor.title')}</h2>

        {!showRecovery ? (
          <>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '0.9rem' }}>
              {t('twoFactor.totpHint')}
            </p>
            <form onSubmit={handleVerify}>
              <input
                className={styles.inputField}
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                maxLength={7}
                placeholder="000 000"
                value={code}
                onChange={e => setCode(e.target.value.replace(/[^0-9 ]/g, ''))}
                autoFocus
                autoComplete="one-time-code"
                style={{ letterSpacing: '0.2em', textAlign: 'center', fontSize: '1.4rem' }}
              />
              {error && <p className={styles.errorMessage}>{error}</p>}
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || code.replace(/\s/g, '').length < 6}
              >
                {loading ? t('twoFactor.verifying') : t('twoFactor.verify')}
              </button>
            </form>
            <button
              type="button"
              className={styles.forgotPasswordLink}
              onClick={() => { setShowRecovery(true); setError(''); }}
            >
              {t('twoFactor.useBackupCode')}
            </button>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px', fontSize: '0.9rem' }}>
              {t('twoFactor.backupHint')}
            </p>
            <form onSubmit={handleRecover}>
              <input
                className={styles.inputField}
                type="text"
                placeholder="XXXX-XXXX"
                value={backupCode}
                onChange={e => setBackupCode(e.target.value.toUpperCase())}
                autoFocus
                style={{ letterSpacing: '0.1em', textAlign: 'center', fontSize: '1.1rem' }}
              />
              {error && <p className={styles.errorMessage}>{error}</p>}
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || backupCode.replace(/-/g, '').length < 8}
              >
                {loading ? t('twoFactor.loggingIn') : t('twoFactor.loginWithBackup')}
              </button>
            </form>
            <button
              type="button"
              className={styles.forgotPasswordLink}
              onClick={() => { setShowRecovery(false); setError(''); }}
            >
              {t('twoFactor.backToTotp')}
            </button>
          </>
        )}

        <button
          type="button"
          className={styles.forgotPasswordLink}
          onClick={onCancel}
          style={{ marginTop: '12px', color: 'var(--text-muted)' }}
        >
          {t('twoFactor.cancelLogin')}
        </button>
      </div>
    </div>
  );
}
